<template>
  <div>
    <div class="d-flex align-items-center mb-1">
      <b-avatar rounded size="32" variant="light-danger" class="mr-75">
        <feather-icon icon="FileTextIcon" size="16" />
      </b-avatar>
      <div>
        <h4 class="mb-0 page-title">
          Proposal
        </h4>
        <b-link class="title" @click="goBackToDetail()"
          ><feather-icon
            icon="ArrowLeftIcon"
            size="16"
            style="margin-bottom:0.3rem;"
          />{{ productName || 'Back to Product Detail' }}</b-link
        >
      </div>
    </div>
    <b-card :title="cardTitle">
      <div class="m-2">
        <div v-if="productName" class="text-muted mb-2">
          File proposal untuk product {{ productName }}
        </div>

        <div class="proposal-product-panel mb-2">
          <div class="proposal-product-panel__title">
            Proposal Product Files
          </div>

          <div
            v-if="proposalProductLoading"
            class="text-center text-secondary py-1"
          >
            <b-spinner small class="mr-50" />
            Loading proposal product...
          </div>

          <div
            v-else-if="proposalProductFiles.length"
            class="proposal-product-list"
          >
            <div
              v-for="(item, idx) in proposalProductFiles"
              :key="item.name || item.usrName || idx"
              class="proposal-product-item"
            >
              <div class="proposal-product-item__body">
                <div class="proposal-product-item__name">
                  {{
                    item.usrName || item.name || `Proposal Product ${idx + 1}`
                  }}
                </div>
                <div class="proposal-product-item__meta">
                  {{ formatFileSize(item.size) }} • {{ item.created_at || '-' }}
                </div>
              </div>

              <div class="proposal-product-item__actions">
                <b-button
                  size="sm"
                  variant="outline-primary"
                  :disabled="isProposalProductOpening(item, idx)"
                  @click="openProposalProductPreview(item, idx)"
                >
                  <b-spinner
                    v-if="isProposalProductOpening(item, idx)"
                    small
                    class="mr-50"
                  />
                  Preview
                </b-button>
                <b-button
                  size="sm"
                  variant="primary"
                  :disabled="isProposalProductDownloading(item, idx)"
                  @click="downloadProposalProductFile(item, idx)"
                >
                  <b-spinner
                    v-if="isProposalProductDownloading(item, idx)"
                    small
                    class="mr-50"
                  />
                  Download
                </b-button>
              </div>
            </div>
          </div>

          <div v-else class="text-muted small">
            Belum ada file proposal product.
          </div>
        </div>
      </div>
    </b-card>
    <b-modal
      ref="modal-document-preview"
      :title="preview.title || 'Document Preview'"
      size="xl"
      centered
      @hidden="clearPreview"
    >
      <div v-if="preview.loading" class="text-center py-2">
        <b-spinner variant="primary" />
      </div>

      <div v-else-if="preview.error" class="text-center text-danger py-2">
        {{ preview.error }}
      </div>

      <div v-else>
        <iframe
          v-if="preview.kind === 'pdf'"
          class="preview-frame"
          :src="preview.url"
          title="Document preview"
        ></iframe>

        <iframe
          v-else-if="preview.kind === 'office' && preview.officeUrl"
          class="preview-frame"
          :src="preview.officeUrl"
          title="Office document preview"
        ></iframe>

        <div v-else-if="preview.kind === 'pptx'" class="pptx-preview-wrapper">
          <div ref="pptxPreviewContainer" class="pptx-preview-container"></div>
        </div>

        <div v-else-if="preview.kind === 'docx'" class="docx-preview-wrapper">
          <div ref="docxPreviewContainer" class="docx-preview-container"></div>
        </div>

        <div v-else-if="preview.kind === 'image'" class="text-center">
          <img
            :src="preview.url"
            alt="Preview"
            class="img-fluid preview-image"
          />
        </div>

        <div v-else class="text-center text-muted py-2">
          Preview tidak tersedia untuk tipe file ini.
        </div>
      </div>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="$refs['modal-document-preview'].hide()"
        >
          Close
        </b-button>
        <b-button
          variant="primary"
          :disabled="!preview.blob"
          @click="downloadPreview"
        >
          Download
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { BCard, BButton, BAvatar, BLink, BSpinner, BModal } from 'bootstrap-vue'
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'

export default {
  components: {
    BCard,
    BButton,
    BAvatar,
    BLink,
    BSpinner,
    BModal,
  },
  data() {
    return {
      productName: '',
      proposalProductFiles: [],
      proposalProductLoading: false,
      proposalProductOpenLoading: [],
      proposalProductDownloadLoading: [],
      previewRequestId: 0,
      pptxPreviewInit: null,
      pptxPreviewer: null,
      docxRenderAsync: null,
      preview: {
        loading: false,
        error: '',
        url: '',
        officeUrl: '',
        blob: null,
        title: '',
        filename: '',
        kind: '',
        mime: '',
        sourceUrl: '',
      },
    }
  },
  computed: {
    cardTitle() {
      return this.productName || 'Product Proposal'
    },
  },
  watch: {
    '$route.params.id': function handleRouteProductIdChange() {
      this.fetchProductContext()
    },
  },
  mounted() {
    this.fetchProductContext()
  },
  beforeDestroy() {
    this.clearPreview()
  },
  methods: {
    getPdfCoverConfig() {
      return {
        maxFileSizeBytes: 100 * 1024 * 1024,
        companyLogoUrl: '/img/logo.png',
        clientLogoUrl: '/img/catalog_logo.png',
        heroImageUrl: '/img/Header.png',
      }
    },
    getProposalProductFileKey(item, idx) {
      return item?.name || item?.usrName || String(idx)
    },
    isProposalProductOpening(item, idx) {
      const key = this.getProposalProductFileKey(item, idx)
      return this.proposalProductOpenLoading.includes(key)
    },
    isProposalProductDownloading(item, idx) {
      const key = this.getProposalProductFileKey(item, idx)
      return this.proposalProductDownloadLoading.includes(key)
    },
    normalizeFilename(value, fallbackName = 'proposal-product') {
      if (!value) return fallbackName
      const trimmed = String(value).trim()
      if (!trimmed) return fallbackName
      const noQuery = trimmed.split('?')[0]
      const lastSegment = noQuery
        .split('/')
        .filter(Boolean)
        .pop()
      const base = lastSegment || noQuery || fallbackName
      return base.replace(/[\\/:*?"<>|]+/g, '_')
    },
    ensureFilenameExtension(filename, sourcePath = '', mime = '') {
      const base = this.normalizeFilename(filename)
      const hasExtension = /\.[a-z0-9]{2,6}$/i.test(base)
      if (hasExtension) return base

      const extFromPath = String(sourcePath || '')
        .split('?')[0]
        .split('.')
        .pop()
      if (extFromPath && extFromPath !== sourcePath) {
        return `${base}.${String(extFromPath).toLowerCase()}`
      }

      const knownByMime = {
        'application/pdf': 'pdf',
        'application/msword': 'doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          'docx',
        'application/vnd.ms-powerpoint': 'ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          'pptx',
      }
      const extFromMime = knownByMime[String(mime || '').toLowerCase()]
      return extFromMime ? `${base}.${extFromMime}` : base
    },
    inferPreviewKind(mime, filename) {
      const type = String(mime || '').toLowerCase()
      if (type.includes('pdf')) return 'pdf'
      if (type.startsWith('image/')) return 'image'
      if (
        type.includes(
          'application/vnd.openxmlformats-officedocument.presentationml.presentation'
        )
      ) {
        return 'pptx'
      }
      if (
        type.includes(
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        )
      ) {
        return 'docx'
      }
      if (type.includes('msword') || type.includes('ms-powerpoint')) {
        return 'office'
      }

      const ext = String(filename || '')
        .toLowerCase()
        .split('?')[0]
        .split('.')
        .pop()

      if (ext === 'pdf') return 'pdf'
      if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
        return 'image'
      }
      if (ext === 'pptx') return 'pptx'
      if (ext === 'docx') return 'docx'
      if (['doc', 'ppt'].includes(ext)) return 'office'
      return 'file'
    },
    buildOfficePreviewUrl(sourceUrl) {
      const raw = String(sourceUrl || '').trim()
      if (!/^https?:\/\//i.test(raw)) return ''
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        raw
      )}`
    },
    startPreview({ title, filename } = {}) {
      this.clearPreview()
      this.previewRequestId += 1
      this.preview.loading = true
      this.preview.error = ''
      this.preview.title = title || 'Document Preview'
      this.preview.filename = filename || 'document'
      this.$refs['modal-document-preview']?.show()
    },
    async resolvePptxPreviewInit() {
      if (typeof this.pptxPreviewInit === 'function')
        return this.pptxPreviewInit
      const module = await import('pptx-preview/dist/pptx-preview.es.js')
      const init = module?.init
      if (typeof init !== 'function') {
        throw new Error('pptx-preview init function is unavailable')
      }
      this.pptxPreviewInit = init
      return init
    },
    async resolveDocxRenderAsync() {
      if (typeof this.docxRenderAsync === 'function')
        return this.docxRenderAsync
      const module = await import('docx-preview/dist/docx-preview.js')
      const renderAsync = module?.renderAsync
      if (typeof renderAsync !== 'function') {
        throw new Error('docx-preview renderAsync is unavailable')
      }
      this.docxRenderAsync = renderAsync
      return renderAsync
    },
    destroyPptxPreviewer() {
      if (
        this.pptxPreviewer &&
        typeof this.pptxPreviewer.destroy === 'function'
      ) {
        this.pptxPreviewer.destroy()
      }
      this.pptxPreviewer = null
      const container = this.$refs.pptxPreviewContainer
      if (container) {
        container.innerHTML = ''
      }
    },
    clearDocxPreviewContainer() {
      const container = this.$refs.docxPreviewContainer
      if (container) {
        container.innerHTML = ''
      }
    },
    clearStructuredPreviewContainers() {
      this.destroyPptxPreviewer()
      this.clearDocxPreviewContainer()
    },
    async blobToArrayBuffer(blob) {
      if (!blob) return null
      if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsArrayBuffer(blob)
      })
    },
    async renderPptxPreview(blob, sourceUrl, requestId) {
      if (!blob || requestId !== this.previewRequestId) return
      try {
        const init = await this.resolvePptxPreviewInit()
        if (requestId !== this.previewRequestId) return
        await this.$nextTick()
        if (requestId !== this.previewRequestId) return
        const container = this.$refs.pptxPreviewContainer
        if (!container) throw new Error('PPTX preview container not found')
        this.destroyPptxPreviewer()
        const arrayBuffer = await this.blobToArrayBuffer(blob)
        if (!arrayBuffer || requestId !== this.previewRequestId) return
        const width = container.clientWidth > 0 ? container.clientWidth : 960
        const previewer = init(container, { width, height: 540, mode: 'list' })
        this.pptxPreviewer = previewer
        await previewer.preview(arrayBuffer)
      } catch (error) {
        const fallbackOfficeUrl = this.buildOfficePreviewUrl(sourceUrl)
        if (fallbackOfficeUrl && requestId === this.previewRequestId) {
          this.preview.kind = 'office'
          this.preview.officeUrl = fallbackOfficeUrl
          this.preview.error = ''
          return
        }
        if (requestId === this.previewRequestId) {
          this.setPreviewError('Gagal merender file PPTX')
        }
      }
    },
    async renderDocxPreview(blob, sourceUrl, requestId) {
      if (!blob || requestId !== this.previewRequestId) return
      try {
        const renderAsync = await this.resolveDocxRenderAsync()
        if (requestId !== this.previewRequestId) return
        await this.$nextTick()
        if (requestId !== this.previewRequestId) return
        const container = this.$refs.docxPreviewContainer
        if (!container) throw new Error('DOCX preview container not found')
        this.clearStructuredPreviewContainers()
        const arrayBuffer = await this.blobToArrayBuffer(blob)
        if (!arrayBuffer || requestId !== this.previewRequestId) return

        await renderAsync(arrayBuffer, container, null, {
          className: 'docx',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
        })
      } catch (error) {
        const fallbackOfficeUrl = this.buildOfficePreviewUrl(sourceUrl)
        if (fallbackOfficeUrl && requestId === this.previewRequestId) {
          this.preview.kind = 'office'
          this.preview.officeUrl = fallbackOfficeUrl
          this.preview.error = ''
          return
        }
        if (requestId === this.previewRequestId) {
          this.setPreviewError('Gagal merender file DOCX')
        }
      }
    },
    setPreviewFromBlob(blob, { mime, filename, sourceUrl } = {}) {
      if (!blob) return
      const resolvedMime = mime || blob.type || ''
      const safeFilename = this.normalizeFilename(
        filename || this.preview.filename || 'document',
        'document'
      )

      if (this.preview.url) {
        try {
          window.URL.revokeObjectURL(this.preview.url)
        } catch (e) {
          // Ignore revoke errors; the object URL is best-effort cleanup.
        }
      }

      this.preview.blob = blob
      this.preview.mime = resolvedMime
      this.preview.filename = safeFilename
      this.preview.kind = this.inferPreviewKind(resolvedMime, safeFilename)
      this.preview.sourceUrl = sourceUrl || ''
      this.preview.officeUrl =
        this.preview.kind === 'office'
          ? this.buildOfficePreviewUrl(sourceUrl)
          : ''
      this.preview.url = window.URL.createObjectURL(blob)
      this.preview.loading = false
      this.preview.error = ''

      if (this.preview.kind === 'pptx') {
        this.renderPptxPreview(blob, sourceUrl, this.previewRequestId)
      } else if (this.preview.kind === 'docx') {
        this.renderDocxPreview(blob, sourceUrl, this.previewRequestId)
      } else {
        this.clearStructuredPreviewContainers()
      }
    },
    setPreviewError(message) {
      this.preview.loading = false
      this.preview.error = message || 'Gagal memuat dokumen'
    },
    clearPreview() {
      this.clearStructuredPreviewContainers()
      if (this.preview?.url) {
        try {
          window.URL.revokeObjectURL(this.preview.url)
        } catch (e) {
          // Ignore revoke errors; the object URL is best-effort cleanup.
        }
      }
      this.preview = {
        loading: false,
        error: '',
        url: '',
        officeUrl: '',
        blob: null,
        title: '',
        filename: '',
        kind: '',
        mime: '',
        sourceUrl: '',
      }
    },
    downloadPreview() {
      if (!this.preview?.blob) return
      const filename = this.normalizeFilename(
        this.preview.filename || 'document'
      )
      const url = window.URL.createObjectURL(this.preview.blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.rel = 'noopener'
      link.click()
      window.URL.revokeObjectURL(url)
    },
    parseAttachmentField(raw) {
      if (!raw) return []
      if (Array.isArray(raw)) return raw.filter(Boolean)
      if (typeof raw === 'object') return [raw]
      if (typeof raw === 'string') {
        const trimmed = raw.trim()
        if (!trimmed) return []
        try {
          const parsed = JSON.parse(trimmed)
          if (Array.isArray(parsed)) return parsed.filter(Boolean)
          if (parsed && typeof parsed === 'object') return [parsed]
          return []
        } catch (error) {
          return [{ link: trimmed, name: trimmed, usrName: trimmed }]
        }
      }
      return []
    },
    resolveAttachmentUrl(path) {
      if (!path) return ''
      const candidate = String(path)
      if (/^https?:\/\//i.test(candidate)) {
        return candidate
      }
      const base = process.env.VUE_APP_API_HOST || ''
      const separator = base.endsWith('/') ? '' : '/'
      const cleanPath = candidate.replace(/^\/+/, '')
      const normalizedPath = cleanPath.startsWith('storage/')
        ? cleanPath
        : `storage/${cleanPath}`
      return `${base}${separator}${normalizedPath}`
    },
    formatFileSize(bytes) {
      if (!bytes) return '-'
      const mb = Number(bytes) / 1000000
      return `${mb.toFixed(2)} MB`
    },
    isPdfFile({ mime = '', filename = '' } = {}) {
      const normalizedMime = String(mime || '').toLowerCase()
      if (normalizedMime.includes('pdf')) return true
      const ext = String(filename || '')
        .toLowerCase()
        .split('?')[0]
        .split('.')
        .pop()
      return ext === 'pdf'
    },
    triggerBlobDownload(blob, filename) {
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      link.rel = 'noopener'
      link.click()
      window.URL.revokeObjectURL(objectUrl)
    },
    async embedImageFromUrl(pdfDoc, url) {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Image is unavailable: ${url}`)
      const bytes = await response.arrayBuffer()
      const contentType = String(response.headers.get('content-type') || '')
      const isJpeg =
        contentType.includes('jpeg') ||
        contentType.includes('jpg') ||
        /\.jpe?g($|\?)/i.test(url)
      return isJpeg ? pdfDoc.embedJpg(bytes) : pdfDoc.embedPng(bytes)
    },
    async createCoverPdfBytes(pageSize = { width: 595.28, height: 841.89 }) {
      const {
        companyLogoUrl,
        clientLogoUrl,
        heroImageUrl,
      } = this.getPdfCoverConfig()
      const coverDocument = await PDFDocument.create()
      const page = coverDocument.addPage([pageSize.width, pageSize.height])
      const pageWidth = page.getWidth()
      const pageHeight = page.getHeight()

      // Background
      page.drawRectangle({
        x: 0,
        y: 0,
        width: pageWidth,
        height: pageHeight,
        color: rgb(0.975, 0.978, 0.985),
      })

      // Top partnership strip
      const marginX = pageWidth * 0.075
      const topStripY = pageHeight * 0.84
      const topStripH = pageHeight * 0.12
      page.drawRectangle({
        x: marginX,
        y: topStripY,
        width: pageWidth - marginX * 2,
        height: topStripH,
        color: rgb(1, 1, 1),
        borderColor: rgb(0.89, 0.91, 0.95),
        borderWidth: 1,
      })

      // Separator between logos
      page.drawRectangle({
        x: pageWidth / 2 - 0.6,
        y: topStripY + 14,
        width: 1.2,
        height: topStripH - 28,
        color: rgb(0.9, 0.92, 0.95),
      })

      // Company logo (left)
      const companyLogo = await this.embedImageFromUrl(
        coverDocument,
        companyLogoUrl
      )
      const leftZoneW = (pageWidth - marginX * 2) / 2
      const logoMaxW = leftZoneW * 0.62
      const companyScale = Math.min(logoMaxW / companyLogo.width, 1)
      const companyW = companyLogo.width * companyScale
      const companyH = companyLogo.height * companyScale
      const companyX = marginX + (leftZoneW - companyW) / 2
      const companyY = topStripY + (topStripH - companyH) / 2
      page.drawImage(companyLogo, {
        x: companyX,
        y: companyY,
        width: companyW,
        height: companyH,
      })

      // Client logo (right)
      const rightZoneX = pageWidth / 2
      try {
        const clientLogo = await this.embedImageFromUrl(
          coverDocument,
          clientLogoUrl
        )
        const clientScale = Math.min(logoMaxW / clientLogo.width, 1)
        const clientW = clientLogo.width * clientScale
        const clientH = clientLogo.height * clientScale
        const clientX = rightZoneX + (leftZoneW - clientW) / 2
        const clientY = topStripY + (topStripH - clientH) / 2
        page.drawImage(clientLogo, {
          x: clientX,
          y: clientY,
          width: clientW,
          height: clientH,
        })
      } catch (e) {
        page.drawRectangle({
          x: rightZoneX + leftZoneW * 0.2,
          y: topStripY + topStripH * 0.28,
          width: leftZoneW * 0.6,
          height: topStripH * 0.44,
          color: rgb(0.97, 0.97, 0.98),
          borderColor: rgb(0.84, 0.86, 0.9),
          borderWidth: 1,
        })
      }

      const titleFont = await coverDocument.embedFont(
        StandardFonts.HelveticaBold
      )
      const subtitleFont = await coverDocument.embedFont(
        StandardFonts.Helvetica
      )
      const labelSize = 10
      page.drawText('Logo Perusahaan', {
        x: marginX + 18,
        y: topStripY + 8,
        size: labelSize,
        font: subtitleFont,
        color: rgb(0.45, 0.47, 0.53),
      })
      page.drawText('Logo Klien', {
        x: pageWidth - marginX - 74,
        y: topStripY + 8,
        size: labelSize,
        font: subtitleFont,
        color: rgb(0.45, 0.47, 0.53),
      })

      // Hero image section
      const heroX = marginX
      const heroW = pageWidth - marginX * 2
      const heroH = pageHeight * 0.42
      const heroY = pageHeight * 0.33
      page.drawRectangle({
        x: heroX,
        y: heroY,
        width: heroW,
        height: heroH,
        color: rgb(0.13, 0.16, 0.21),
      })

      try {
        const heroImage = await this.embedImageFromUrl(
          coverDocument,
          heroImageUrl
        )
        const heroScale = Math.max(
          heroW / heroImage.width,
          heroH / heroImage.height
        )
        const drawW = heroImage.width * heroScale
        const drawH = heroImage.height * heroScale
        page.drawImage(heroImage, {
          x: heroX + (heroW - drawW) / 2,
          y: heroY + (heroH - drawH) / 2,
          width: drawW,
          height: drawH,
        })
      } catch (e) {
        // Keep dark block fallback if hero image fails.
      }

      // Overlay for better text readability
      page.drawRectangle({
        x: heroX,
        y: heroY,
        width: heroW,
        height: heroH,
        color: rgb(0.08, 0.11, 0.16),
        opacity: 0.42,
      })

      // Light network-like lines
      for (let i = 0; i < 9; i += 1) {
        const startX = heroX + (heroW / 10) * i
        page.drawRectangle({
          x: startX,
          y: heroY,
          width: 1,
          height: heroH,
          color: rgb(0.6, 0.76, 0.96),
          opacity: 0.17,
        })
      }

      const titleSize = Math.max(26, Math.round(pageWidth * 0.052))
      const title = 'PROPOSAL SOLUSI DIGITAL'
      const titleWidth = titleFont.widthOfTextAtSize(title, titleSize)
      page.drawText(title, {
        x: (pageWidth - titleWidth) / 2,
        y: heroY + heroH * 0.56,
        size: titleSize,
        font: titleFont,
        color: rgb(1, 1, 1),
      })

      const subtitle = 'Optimalisasi Konektivitas Berkecepatan Tinggi'
      const subtitleSize = Math.max(12, Math.round(pageWidth * 0.022))
      const subtitleWidth = subtitleFont.widthOfTextAtSize(
        subtitle,
        subtitleSize
      )
      page.drawText(subtitle, {
        x: (pageWidth - subtitleWidth) / 2,
        y: heroY + heroH * 0.43,
        size: subtitleSize,
        font: subtitleFont,
        color: rgb(0.9, 0.94, 0.99),
      })

      // Bottom accent
      const accentY = pageHeight * 0.2
      const accentH = Math.max(10, pageHeight * 0.015)
      page.drawRectangle({
        x: marginX,
        y: accentY,
        width: pageWidth - marginX * 2,
        height: accentH,
        color: rgb(0.88, 0.08, 0.16),
      })

      const preparedBy = 'Disiapkan Oleh: Product Catalog | 2026'
      const preparedSize = Math.max(11, Math.round(pageWidth * 0.018))
      const preparedWidth = subtitleFont.widthOfTextAtSize(
        preparedBy,
        preparedSize
      )
      page.drawText(preparedBy, {
        x: (pageWidth - preparedWidth) / 2,
        y: accentY - preparedSize - 10,
        size: preparedSize,
        font: subtitleFont,
        color: rgb(0.22, 0.25, 0.32),
      })

      return coverDocument.save()
    },
    async prependCoverToPdfBlob(pdfBlob) {
      const sourceBytes = await pdfBlob.arrayBuffer()

      const sourcePdf = await PDFDocument.load(sourceBytes)
      const firstPage = sourcePdf.getPage(0)
      const { width, height } = firstPage.getSize()
      const coverBytes = await this.createCoverPdfBytes({ width, height })

      const outputPdf = await PDFDocument.create()
      const coverPdf = await PDFDocument.load(coverBytes)

      const coverPages = await outputPdf.copyPages(
        coverPdf,
        coverPdf.getPageIndices()
      )
      coverPages.forEach(page => outputPdf.addPage(page))

      const sourcePages = await outputPdf.copyPages(
        sourcePdf,
        sourcePdf.getPageIndices()
      )
      sourcePages.forEach(page => outputPdf.addPage(page))

      const mergedBytes = await outputPdf.save()
      return new Blob([mergedBytes], { type: 'application/pdf' })
    },
    async downloadWithOptionalPdfCover(blob, filename, mime = '') {
      const config = this.getPdfCoverConfig()
      const isPdf = this.isPdfFile({ mime, filename })
      const isWithinLimit = Number(blob?.size || 0) <= config.maxFileSizeBytes

      if (!isPdf || !isWithinLimit) {
        this.triggerBlobDownload(blob, filename)
        return
      }

      try {
        const mergedBlob = await this.prependCoverToPdfBlob(blob)
        this.triggerBlobDownload(mergedBlob, filename)
      } catch (error) {
        this.triggerBlobDownload(blob, filename)
      }
    },
    async readBlobText(blob) {
      if (!blob) return ''
      if (typeof blob.text === 'function') {
        return blob.text()
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result || '')
        reader.onerror = reject
        reader.readAsText(blob)
      })
    },
    async getErrorMessage(error) {
      const fallback =
        error?.message || error?.statusText || 'Gagal mengunduh file proposal'

      const data = error?.data ?? error?.response?.data
      if (!data) return fallback

      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data)
          if (parsed?.message) return parsed.message
        } catch (e) {
          // ignore
        }
        return data || fallback
      }

      if (data instanceof Blob) {
        try {
          const text = await this.readBlobText(data)
          if (!text) return fallback
          try {
            const parsed = JSON.parse(text)
            if (parsed?.message) return parsed.message
          } catch (e) {
            // ignore
          }
          return text
        } catch (e) {
          return fallback
        }
      }

      if (typeof data === 'object' && data?.message) {
        return data.message
      }

      return fallback
    },
    shouldRetryExpiredDownload(status, message) {
      if (Number(status) !== 403) return false
      const text = String(message || '').toLowerCase()
      return (
        text.includes('went idle') ||
        text.includes('idle for too long') ||
        text.includes('refresh the product detail')
      )
    },
    async fetchLatestProposalProductFiles() {
      const response = await this.$http.post(
        `/product/browse/id/${this.$route.params.id}/set/first`,
        {
          map: {
            PRODUCT: 'd',
            proposal_product: 'd',
          },
        },
        { skipGlobalErrorNotif: true }
      )

      const records = response?.data?.data?.records || {}
      this.productName = records.PRODUCT || this.productName
      this.proposalProductFiles = this.parseAttachmentField(
        records.proposal_product
      )
      return this.proposalProductFiles
    },
    async fetchProductContext() {
      this.proposalProductLoading = true
      try {
        const response = await this.$http.post(
          `/product/browse/id/${this.$route.params.id}/set/first`,
          {
            map: {
              PRODUCT: 'd',
              proposal_product: 'd',
            },
          }
        )
        const records = response?.data?.data?.records || {}
        this.productName = records.PRODUCT || ''
        this.proposalProductFiles = this.parseAttachmentField(
          records.proposal_product
        )
        this.$store.commit('app/SET_PAGE_TITLE', this.productName || '')
      } catch (error) {
        console.error(error)
      } finally {
        this.proposalProductLoading = false
      }
    },
    async openProposalProductPreview(item, idx) {
      const fileKey = this.getProposalProductFileKey(item, idx)
      const url = this.resolveAttachmentUrl(
        item?.link || item?.file_url || item?.path || item?.name
      )
      if (!url) return

      const filename = this.ensureFilenameExtension(
        item?.usrName || item?.name || 'proposal-product',
        item?.name || item?.file_url || item?.path || item?.link,
        item?.type
      )

      this.startPreview({
        title: item?.usrName || filename,
        filename,
      })

      if (!this.proposalProductOpenLoading.includes(fileKey)) {
        this.proposalProductOpenLoading.push(fileKey)
      }

      try {
        const response = await this.$http.get(url, {
          responseType: 'blob',
          skipGlobalErrorNotif: true,
        })
        const contentType = response?.headers?.['content-type'] || ''
        let blob
        if (response?.data instanceof Blob && response.data.type) {
          blob = response.data
        } else {
          blob = new Blob([response?.data], { type: contentType })
        }

        this.setPreviewFromBlob(blob, {
          mime: item?.type || blob?.type || contentType,
          filename,
          sourceUrl: url,
        })
      } catch (error) {
        this.setPreviewError('Gagal membuka file proposal')
      } finally {
        this.proposalProductOpenLoading = this.proposalProductOpenLoading.filter(
          key => key !== fileKey
        )
      }
    },
    async downloadProposalProductFile(item, idx) {
      const fileKey = this.getProposalProductFileKey(item, idx)
      const url = this.resolveAttachmentUrl(
        item?.link || item?.file_url || item?.path || item?.name
      )
      if (!url) return

      if (!this.proposalProductDownloadLoading.includes(fileKey)) {
        this.proposalProductDownloadLoading.push(fileKey)
      }

      try {
        const response = await this.$http.get(url, {
          responseType: 'blob',
          skipGlobalErrorNotif: true,
        })

        const blob =
          response?.data instanceof Blob
            ? response.data
            : new Blob([response?.data], {
                type: response?.headers?.['content-type'] || item?.type || '',
              })
        const rawName =
          item?.usrName || item?.name || item?.file_url || item?.path
        const filename = this.ensureFilenameExtension(
          rawName,
          item?.name || item?.file_url || item?.path || item?.link,
          blob?.type || item?.type
        )

        await this.downloadWithOptionalPdfCover(
          blob,
          filename,
          blob?.type || item?.type
        )
      } catch (error) {
        const status = error?.status || error?.response?.status || 0
        const message = await this.getErrorMessage(error)

        if (this.shouldRetryExpiredDownload(status, message)) {
          try {
            const refreshedFiles = await this.fetchLatestProposalProductFiles()
            const refreshedItem =
              (refreshedFiles || []).find(
                x =>
                  x?.name === item?.name ||
                  x?.usrName === item?.usrName ||
                  x?.name === fileKey ||
                  x?.usrName === fileKey
              ) || refreshedFiles?.[idx]

            const refreshedUrl = this.resolveAttachmentUrl(
              refreshedItem?.link ||
                refreshedItem?.file_url ||
                refreshedItem?.path ||
                refreshedItem?.name
            )

            if (refreshedUrl) {
              const retryResponse = await this.$http.get(refreshedUrl, {
                responseType: 'blob',
                skipGlobalErrorNotif: true,
              })
              const retryBlob =
                retryResponse?.data instanceof Blob
                  ? retryResponse.data
                  : new Blob([retryResponse?.data], {
                      type:
                        retryResponse?.headers?.['content-type'] ||
                        refreshedItem?.type ||
                        '',
                    })
              const retryRawName =
                refreshedItem?.usrName ||
                refreshedItem?.name ||
                refreshedItem?.file_url ||
                refreshedItem?.path
              const retryFilename = this.ensureFilenameExtension(
                retryRawName,
                refreshedItem?.name ||
                  refreshedItem?.file_url ||
                  refreshedItem?.path ||
                  refreshedItem?.link,
                retryBlob?.type || refreshedItem?.type
              )

              await this.downloadWithOptionalPdfCover(
                retryBlob,
                retryFilename,
                retryBlob?.type || refreshedItem?.type
              )
              return
            }
          } catch (retryError) {
            // fall through to toast
          }
        }

        this.$bvToast.toast(message || 'Gagal mengunduh file proposal', {
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.proposalProductDownloadLoading = this.proposalProductDownloadLoading.filter(
          key => key !== fileKey
        )
      }
    },
    goBackToDetail() {
      const fromRoute = this.$route?.query?.from
      const productId = this.$route.params.id
      if (fromRoute) {
        this.$router.push({
          name: fromRoute,
          params: { key: productId },
        })
        return
      }
      this.$router.push({
        name: 'product-detail',
        params: { key: productId },
      })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.proposal-product-panel {
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  background: #fafafc;
}

.proposal-product-panel__title {
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #5e5873;
}

.proposal-product-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.proposal-product-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  background: #fff;
  border: 1px solid #ebe9f1;
}

.proposal-product-item__body {
  flex: 1;
  min-width: 0;
}

.proposal-product-item__name {
  font-weight: 600;
  color: #5e5873;
  word-break: break-word;
}

.proposal-product-item__meta {
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #6e6b7b;
}

.proposal-product-item__actions {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.preview-frame {
  width: 100%;
  height: 75vh;
  border: 0;
  border-radius: 12px;
  background: #0b1220;
}

.preview-image {
  max-height: 75vh;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
}

.pptx-preview-wrapper,
.docx-preview-wrapper {
  height: 75vh;
  overflow: auto;
  border-radius: 12px;
  background: #f8fafc;
  padding: 0.75rem;
}

.pptx-preview-container,
.docx-preview-container {
  min-height: 100%;
}

.title {
  color: black;
}

@media (max-width: 767px) {
  .proposal-product-item {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
